/** @jsxImportSource theme-ui */
import { Box, Flex, Heading, Label, Text } from 'theme-ui';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { addMonths, format } from 'date-fns';
import Button from '@/components/Button';
import CustomDatePicker from '@/components/CustomDatePicker';
import { GrayFormikField } from '@/components/GrayFormikField';
import { useScheduleCompanyDeletionMutation } from '@/store/apiService';
import { setMarkCompanyId } from '@/store/markForDeletion';
import { LicenseTerminationTiming } from '@/types/types';

const LEAVE_LICENSES = 'LeaveLicenses';
type LicenseAction = LicenseTerminationTiming | typeof LEAVE_LICENSES;

export const LICENSE_ACTION_OPTIONS: { label: string; value: LicenseAction }[] = [
    { label: 'Leave licenses as they are', value: LEAVE_LICENSES },
    { label: 'End at the end of the billing period', value: 'AtSubscriptionBillingPeriodEnd' },
    { label: 'End immediately', value: 'Immediately' },
];

const pad = (n: number) => String(n).padStart(2, '0');
export const toLocalDateTimeString = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;

export const defaultScheduledDeletionDate = () => toLocalDateTimeString(addMonths(new Date(), 1));

const MarkForDeletionDialog = () => {
    const companyId: string = useSelector((s: any) => s.markForDeletion.companyId);
    const inactivityNotificationDate: string | null = useSelector(
        (s: any) => s.markForDeletion.inactivityNotificationDate ?? null
    );
    const dispatch = useDispatch();
    const dialog = useDialogState();
    const wasVisible = useRef(false);
    const [scheduleDeletion, scheduleDeletionState] = useScheduleCompanyDeletionMutation();

    useEffect(() => {
        if (companyId) {
            dialog.show();
        } else {
            dialog.hide();
        }
    }, [companyId]);

    useEffect(() => {
        if (dialog.visible) {
            wasVisible.current = true;
            return;
        }
        if (wasVisible.current) {
            wasVisible.current = false;
            dispatch(setMarkCompanyId());
        }
    }, [dialog.visible]);

    if (!companyId) {
        return null;
    }

    return (
        <DialogBackdrop {...dialog}>
            <Dialog
                {...dialog}
                aria-label="Mark company for deletion"
                sx={{
                    '&&': {
                        padding: 4,
                        maxWidth: '560px',
                        width: '100%',
                    },
                }}
            >
                <Heading as="h3" mb={3}>
                    Mark company for deletion
                </Heading>
                <Text as="p" mb={3} sx={{ fontSize: 1, color: 'gray.6' }}>
                    The company is deleted automatically on the scheduled date. Until then the mark
                    can be cancelled from the company page.
                </Text>

                <Formik
                    initialValues={{
                        Reason: '',
                        LicenseAction: LEAVE_LICENSES as LicenseAction,
                        CloseCompanyNow: false,
                        NotifyCompany: true,
                        ScheduledDeletionDate: defaultScheduledDeletionDate(),
                        AcknowledgeInactivityWarning: false,
                    }}
                    validate={(values) => {
                        const errors: Record<string, string> = {};
                        if (!values.Reason.trim()) {
                            errors.Reason = 'A reason is required';
                        }
                        if (!values.ScheduledDeletionDate) {
                            errors.ScheduledDeletionDate = 'Select a deletion date';
                        }
                        if (inactivityNotificationDate && !values.AcknowledgeInactivityWarning) {
                            errors.AcknowledgeInactivityWarning =
                                'Confirm that the chosen date replaces the automatic deletion';
                        }
                        return errors;
                    }}
                    onSubmit={(values, { setSubmitting }) => {
                        scheduleDeletion({
                            CompanyId: companyId,
                            Reason: values.Reason.trim(),
                            LicenseTerminationTiming:
                                values.LicenseAction === LEAVE_LICENSES
                                    ? null
                                    : values.LicenseAction,
                            CloseCompanyNow: values.CloseCompanyNow,
                            NotifyCompany: values.NotifyCompany,
                            ScheduledDeletionDate: values.ScheduledDeletionDate,
                            AcknowledgeInactivityWarning: values.AcknowledgeInactivityWarning,
                        })
                            .unwrap()
                            .then((result) => {
                                setSubmitting(false);
                                const date = result?.ScheduledDeletionDate
                                    ? format(new Date(result.ScheduledDeletionDate), 'd MMM yyyy')
                                    : 'the scheduled date';
                                toast.success(`Company marked for deletion on ${date}`, {
                                    autoClose: 5000,
                                });
                                (result?.Warnings ?? []).forEach((warning) =>
                                    toast.warn(warning, { autoClose: false })
                                );
                                dialog.hide();
                            })
                            .catch((err) => {
                                setSubmitting(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                        err?.message ??
                                        'Marking the company for deletion failed',
                                    { autoClose: 8000 }
                                );
                            });
                    }}
                >
                    {(formProps) => {
                        const dateValue = formProps.values.ScheduledDeletionDate
                            ? new Date(formProps.values.ScheduledDeletionDate)
                            : null;

                        return (
                            <form onSubmit={formProps.handleSubmit}>
                                <Box mb={3}>
                                    <Text as="p" sx={{ fontSize: 1, color: 'gray.6' }}>
                                        Company ID: {companyId}
                                    </Text>
                                </Box>

                                {inactivityNotificationDate ? (
                                    <Box
                                        mb={3}
                                        p={3}
                                        sx={{
                                            borderLeft: '4px solid',
                                            borderLeftColor: 'danger',
                                            bg: 'gray.1',
                                            borderRadius: 'xsm',
                                        }}
                                        data-testid="inactivity-warning-notice"
                                    >
                                        <Text as="p" mb={2} sx={{ fontWeight: 'bold' }}>
                                            The inactivity job already warned this company on{' '}
                                            {format(new Date(inactivityNotificationDate), 'd MMM yyyy')}
                                            .
                                        </Text>
                                        <Text as="p" mb={2} sx={{ fontSize: 1 }}>
                                            Marking it replaces that automatic deletion: the date you
                                            choose below takes precedence and the inactivity job leaves
                                            the company alone while the mark is active. Do you want to
                                            proceed?
                                        </Text>
                                        <GrayFormikField
                                            type="checkbox"
                                            name="AcknowledgeInactivityWarning"
                                            label="Yes, replace the automatic deletion with the date below"
                                        />
                                    </Box>
                                ) : null}

                                <GrayFormikField
                                    type="textarea"
                                    name="Reason"
                                    label="Reason"
                                    required
                                    placeholder="Why is the company being deleted?"
                                />

                                <GrayFormikField
                                    type="radio"
                                    name="LicenseAction"
                                    label="What happens to the licenses now?"
                                    options={LICENSE_ACTION_OPTIONS}
                                />

                                <Box mb={3}>
                                    <Label mb={1}>Deletion date</Label>
                                    <CustomDatePicker
                                        variation="normal"
                                        field={{
                                            name: 'ScheduledDeletionDate',
                                            value: dateValue,
                                        }}
                                        onChange={(value: Date | null) => {
                                            formProps.setFieldValue(
                                                'ScheduledDeletionDate',
                                                value ? toLocalDateTimeString(value) : null
                                            );
                                        }}
                                    />
                                    {formProps.errors.ScheduledDeletionDate ? (
                                        <Text color="danger">
                                            {formProps.errors.ScheduledDeletionDate}
                                        </Text>
                                    ) : null}
                                    <Text as="p" sx={{ fontSize: 1, color: 'gray.6' }}>
                                        Defaults to one month from today.
                                    </Text>
                                </Box>

                                <GrayFormikField
                                    type="checkbox"
                                    name="NotifyCompany"
                                    label="Send the account-deletion warning email to all company users"
                                />
                                <GrayFormikField
                                    type="checkbox"
                                    name="CloseCompanyNow"
                                    label="Close the company now (hides the company from the list)"
                                />

                                <Flex sx={{ justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                    <Button type="button" variant="secondary" onClick={dialog.hide}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="danger"
                                        disabled={
                                            !formProps.isValid ||
                                            !formProps.values.Reason.trim() ||
                                            (!!inactivityNotificationDate &&
                                                !formProps.values.AcknowledgeInactivityWarning)
                                        }
                                        isLoading={
                                            formProps.isSubmitting ||
                                            scheduleDeletionState.isLoading
                                        }
                                    >
                                        Mark for deletion
                                    </Button>
                                </Flex>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog>
        </DialogBackdrop>
    );
};

export default MarkForDeletionDialog;
